
import { useState, useEffect, useMemo, useCallback } from 'react';
import { PaymentService } from '@/services/payment';
import { useAuth } from '@/hooks/useAuth';
import { useConnectionQuality } from '@/hooks/useConnectionQuality';
import { useOnlineStatus } from '@/utils/connectionUtils';
import { useNotify } from '@/hooks/useNotify';
import { Payment } from '@/services/payment/types';
import { PaymentMethod } from '@/types/constants';
import { addDays } from 'date-fns';

export const usePaymentList = () => {
  const { user } = useAuth();
  const { isSlowConnection } = useConnectionQuality();
  const isOnline = useOnlineStatus();
  const { error: notifyError } = useNotify();
  
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [pageSize, setPageSize] = useState(isSlowConnection ? 10 : 20);
  const [dateRange, setDateRange] = useState<{ from: Date; to: Date } | undefined>(undefined);
  const [status, setStatus] = useState<string | undefined>(undefined);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | undefined>(undefined);
  
  const pageSizeOptions = useMemo(() => 
    isSlowConnection ? [5, 10, 20] : [10, 20, 50], 
    [isSlowConnection]
  );

  const loadPayments = useCallback(async (
    pageNumber: number, 
    itemsPerPage: number,
    dateFilter?: { from: Date; to: Date },
    statusFilter?: string,
    paymentMethodFilter?: PaymentMethod
  ) => {
    if (!isOnline) {
      notifyError('You are offline', {
        description: 'Cannot load payments while offline'
      });
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      
      const organizationId = user?.user_metadata?.organization_id;
      if (!organizationId) {
        console.warn("No organization ID found for current user");
        setLoading(false);
        return;
      }
      
      let startDate: string | undefined;
      let endDate: string | undefined;
      
      if (dateFilter) {
        startDate = dateFilter.from.toISOString().split('T')[0];
        // Add one day to include the end date in the results
        const adjustedEndDate = addDays(dateFilter.to, 1);
        endDate = adjustedEndDate.toISOString().split('T')[0];
      }
      
      const { data, totalCount } = await PaymentService.getPaymentsForOrganization(
        organizationId, 
        pageNumber,
        itemsPerPage,
        startDate,
        endDate,
        statusFilter,
        paymentMethodFilter
      );
      
      setPayments(data);
      setTotalItems(totalCount);
      setTotalPages(Math.ceil(totalCount / itemsPerPage));
    } catch (err) {
      console.error("Error loading payments:", err);
      notifyError('Failed to load payments', {
        description: 'Please try again later'
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [isOnline, notifyError, user?.user_metadata?.organization_id]);

  useEffect(() => {
    loadPayments(page, pageSize, dateRange, status, paymentMethod);
  }, [page, pageSize, loadPayments, dateRange, status, paymentMethod]);

  const handleRefresh = useCallback(() => {
    setRefreshing(true);
    
    const organizationId = user?.user_metadata?.organization_id;
    if (organizationId) {
      PaymentService.clearCache(undefined, organizationId);
    }
    
    loadPayments(page, pageSize, dateRange, status, paymentMethod);
  }, [loadPayments, page, pageSize, user?.user_metadata?.organization_id, dateRange, status, paymentMethod]);

  const handlePageChange = useCallback((newPage: number) => {
    if (newPage < 1 || newPage > totalPages) return;
    setPage(newPage);
  }, [totalPages]);

  const handlePageSizeChange = useCallback((newSize: string) => {
    const size = parseInt(newSize);
    setPageSize(size);
    setPage(1);
  }, []);

  const handleDateRangeChange = useCallback((range: { from: Date; to: Date } | undefined) => {
    setDateRange(range);
    setPage(1);
  }, []);

  const handleStatusChange = useCallback((newStatus: string | undefined) => {
    setStatus(newStatus);
    setPage(1);
  }, []);

  const handlePaymentMethodChange = useCallback((newPaymentMethod: PaymentMethod | undefined) => {
    setPaymentMethod(newPaymentMethod);
    setPage(1);
  }, []);

  const handleResetFilters = useCallback(() => {
    setDateRange(undefined);
    setStatus(undefined);
    setPaymentMethod(undefined);
    setPage(1);
  }, []);

  return {
    payments,
    loading,
    refreshing,
    page,
    pageSize,
    totalPages,
    totalItems,
    pageSizeOptions,
    isOnline,
    dateRange,
    status,
    paymentMethod,
    handleRefresh,
    handlePageChange,
    handlePageSizeChange,
    handleDateRangeChange,
    handleStatusChange,
    handlePaymentMethodChange,
    handleResetFilters,
  };
};
